import React, { useEffect, useState } from 'react';
import { BrandedCard, BrandedInput, BrandedButton, BootPulse } from '../components';
import { kratosService, KratosFlow } from '../services/kratosService';
import './Recovery.css';

const hasNode = (flow: KratosFlow | null, name: string): boolean =>
  !!flow?.ui?.nodes?.some((node) => node.attributes?.name === name);

/**
 * /recovery — Ory Kratos self-service recovery (code strategy): email in,
 * code back, then continue to the account. Fixes the 404 Login's
 * "Forgot password?" link pointed at (spec A13).
 */
export const Recovery: React.FC = () => {
  const [flow, setFlow] = useState<KratosFlow | null>(null);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const initialFlow = await kratosService.initRecoveryFlow();
        setFlow(initialFlow);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unable to start account recovery.');
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  const handleSubmitEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!flow) return;
    setError('');
    setSubmitting(true);

    try {
      const updated = await kratosService.submitRecovery(flow.id, { method: 'code', email });
      setFlow(updated);
      setInfo('Check your email for a recovery code.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to send a recovery code.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!flow) return;
    setError('');
    setSubmitting(true);

    try {
      const updated = await kratosService.submitRecovery(flow.id, { method: 'code', code });
      if (updated.redirect_browser_to) {
        window.location.href = updated.redirect_browser_to;
        return;
      }
      setFlow(updated);
      setDone(true);
      setInfo('Your account has been recovered. You can now set a new password.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That code was not valid.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <BrandedCard>
        <BootPulse label="Loading account recovery" />
      </BrandedCard>
    );
  }

  const awaitingCode = !done && hasNode(flow, 'code');

  return (
    <BrandedCard title="Account Recovery">
      {error && (
        <div className="recovery-error" role="alert">
          {error}
        </div>
      )}
      {info && !error && (
        <div className="recovery-info" role="status">
          {info}
        </div>
      )}

      {done ? (
        <div className="recovery-links">
          <a href="/settings" className="recovery-link">
            Continue to your account
          </a>
        </div>
      ) : awaitingCode ? (
        <form onSubmit={handleSubmitCode} className="recovery-form">
          <BrandedInput
            type="text"
            name="code"
            label="Recovery code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Enter the code we emailed you"
            required
            autoComplete="one-time-code"
          />
          <BrandedButton type="submit" variant="primary" fullWidth disabled={submitting}>
            {submitting ? 'Verifying...' : 'Verify code'}
          </BrandedButton>
        </form>
      ) : (
        <form onSubmit={handleSubmitEmail} className="recovery-form">
          <BrandedInput
            type="email"
            name="email"
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter the email on your account"
            required
            autoComplete="email"
          />
          <BrandedButton type="submit" variant="primary" fullWidth disabled={submitting}>
            {submitting ? 'Sending...' : 'Send recovery code'}
          </BrandedButton>
        </form>
      )}

      <div className="recovery-links">
        <a href="/login" className="recovery-link">
          Back to sign in
        </a>
      </div>
    </BrandedCard>
  );
};
